import type { Desk, Workspace } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowRight, Building2, Plus, SquareStack } from 'lucide-react';
import { CreateWorkspaceDialog } from '@/components/create-workspace-dialog';
import { DeskCard, NewDeskCard } from '@/components/desk-card';
import { CreateDeskDialog } from '@/components/desk-dialogs';
import { Page, PageHeader } from '@/components/page';
import { EmptyState, ErrorState, LoadingPage, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { findAccount, useMe } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeSubscription } from '@/lib/realtime';

/** `/$account/desks` — all desks of the account, grouped by workspace. */
export function AccountDesksPage() {
  const { account: param } = useParams({ from: '/_authed/$account/desks' });
  const me = useMe();
  const account = me.data ? findAccount(me.data.accounts, param) : undefined;
  const accountId = account?.id ?? '';

  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId),
    queryFn: () => api.accounts.workspaces(accountId),
    enabled: !!accountId,
  });
  const desks = useQuery({
    queryKey: qk.accountDesks(accountId),
    queryFn: () => api.accounts.desks(accountId),
    enabled: !!accountId,
  });

  // The shell resolves unknown accounts; here we only wait for data.
  if (!account) return <LoadingPage />;

  const wsList = workspaces.data ?? [];
  const groups = groupByWorkspace(wsList, desks.data ?? []);
  const total = desks.data?.length ?? 0;

  return (
    <Page wide>
      {wsList.map((w) => (
        <LiveWorkspace key={w.id} workspaceId={w.id} />
      ))}
      <PageHeader
        eyebrow={account.name}
        title={t('Desks')}
        description={t('A desk is a responsibility you hand over to Deskworks. Here you see all desks from every Workspace in this Account.')}
        actions={
          wsList.length > 0 && (
            <CreateDeskDialog workspaces={wsList} accountId={accountId}>
              <Button>
                <Plus /> {t('Create desk')}
              </Button>
            </CreateDeskDialog>
          )
        }
      />

      {workspaces.isPending || desks.isPending ? (
        <LoadingRows rows={4} />
      ) : workspaces.isError ? (
        <ErrorState error={workspaces.error} onRetry={() => void workspaces.refetch()} />
      ) : desks.isError ? (
        <ErrorState error={desks.error} onRetry={() => void desks.refetch()} />
      ) : wsList.length === 0 ? (
        <EmptyState
          icon={<Building2 />}
          title={t('No Workspace yet')}
          description={t('Desks live in Workspaces — areas like “Marketing” or “Support”. Create a Workspace first.')}
          action={
            me.data && (
              <CreateWorkspaceDialog accounts={me.data.accounts} defaultAccountId={accountId}>
                <Button>{t('Create workspace')}</Button>
              </CreateWorkspaceDialog>
            )
          }
        />
      ) : total === 0 ? (
        <EmptyState
          icon={<SquareStack />}
          title={t('{account} has no desks yet', { account: account.name })}
          description={t('Create your first desk — for example for support requests, daily reports or lead research.')}
          action={
            <CreateDeskDialog workspaces={wsList} accountId={accountId}>
              <Button>
                <Plus /> {t('Create your first desk')}
              </Button>
            </CreateDeskDialog>
          }
        />
      ) : (
        <div className="flex flex-col gap-8">
          {groups.map(({ workspace, workspaceName, desks: list }) => (
            <section key={workspace?.id ?? workspaceName}>
              <div className="mb-3 flex items-center justify-between gap-3 border-b pb-2">
                <div className="flex min-w-0 items-baseline gap-2">
                  {workspace ? (
                    <Link
                      to="/w/$workspaceId"
                      params={{ workspaceId: workspace.id }}
                      className="group inline-flex min-w-0 items-center gap-1.5 text-base font-semibold hover:text-primary"
                    >
                      <span className="truncate">{workspace.name}</span>
                      <ArrowRight className="size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  ) : (
                    <span className="truncate text-base font-semibold">{workspaceName}</span>
                  )}
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {list.length === 1 ? t('1 Desk') : t('{n} Desks', { n: list.length })}
                  </span>
                </div>
                {workspace && (
                  <CreateDeskDialog workspaces={wsList} defaultWorkspaceId={workspace.id} accountId={accountId}>
                    <Button variant="ghost" size="xs">
                      <Plus /> {t('Desk')}
                    </Button>
                  </CreateDeskDialog>
                )}
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {list.map((d) => (
                  <DeskCard key={d.id} desk={d} />
                ))}
                {workspace && <NewDeskCard workspaceId={workspace.id} />}
              </div>
            </section>
          ))}
        </div>
      )}
    </Page>
  );
}

/** Joins the realtime room of one workspace so desk status and counts stay fresh. */
function LiveWorkspace({ workspaceId }: { workspaceId: string }) {
  useRealtimeSubscription(workspaceId);
  return null;
}

/** Groups desks by workspace in the order of the workspace list; workspaces without desks are kept (shown with a hint). */
function groupByWorkspace(workspaces: Workspace[], desks: Desk[]) {
  const byWs = new Map<string, Desk[]>();
  for (const d of desks) {
    const list = byWs.get(d.workspaceId) ?? [];
    list.push(d);
    byWs.set(d.workspaceId, list);
  }
  const groups: { workspace: Workspace | null; workspaceName: string; desks: Desk[] }[] = workspaces.map((w) => ({
    workspace: w,
    workspaceName: w.name,
    desks: byWs.get(w.id) ?? [],
  }));
  // Desks from workspaces missing in the list (shouldn't happen) still show up.
  const known = new Set(workspaces.map((w) => w.id));
  for (const [wsId, list] of byWs) {
    if (!known.has(wsId)) groups.push({ workspace: null, workspaceName: list[0]?.workspaceName ?? t('Workspace'), desks: list });
  }
  return groups;
}
